import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { budget, budgetMonthPath, type HistoryMonth } from '../lib/budget';
import { useLiveReload } from '../lib/live-context';
import { formatCents } from '../lib/money';

export function History() {
  const [months, setMonths] = useState<HistoryMonth[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    const r = await budget.history();
    setMonths(r.months);
  }, []);

  useEffect(() => {
    load().catch((e: unknown) => setError(e instanceof Error ? e.message : 'Could not load history.'));
  }, [load]);

  useLiveReload(() => {
    void load().catch((e: unknown) => setError(e instanceof Error ? e.message : 'Could not load history.'));
  });

  if (error) return <main className="page"><p className="error">{error}</p></main>;
  if (!months) return <main className="page" aria-busy="true" />;

  if (months.length === 0) {
    return (
      <main className="page">
        <h1>History</h1>
        <p className="hint">Nothing recorded yet. Months appear here once they have a limit or an expense.</p>
      </main>
    );
  }

  // Group by year so a long list stays scannable.
  const byYear = new Map<number, HistoryMonth[]>();
  for (const m of months) {
    const list = byYear.get(m.year) ?? [];
    list.push(m);
    byYear.set(m.year, list);
  }

  return (
    <main className="page">
      <h1>History</h1>
      <p className="hint">Tap a month to open it in Budget.</p>

      {[...byYear.entries()].map(([year, rows]) => (
        <section className="panel" key={year}>
          <h2>{year}</h2>
          <ul className="list history">
            {rows.map((m) => (
              <MonthRow key={`${m.year}-${m.month}`} month={m} />
            ))}
          </ul>
        </section>
      ))}
    </main>
  );
}

function MonthRow({ month }: { month: HistoryMonth }) {
  const name = new Date(Date.UTC(month.year, month.month - 1, 1)).toLocaleDateString('en-US', {
    month: 'long',
    timeZone: 'UTC',
  });

  const pct =
    month.limitCents > 0
      ? Math.min(100, Math.round((month.spentCents / month.limitCents) * 100))
      : month.spentCents > 0
        ? 100
        : 0;

  return (
    <li>
      <Link
        className="history-row"
        to={budgetMonthPath(month.year, month.month)}
        state={{ fromHistory: true }}
        aria-label={`Open ${name} ${month.year} in Budget`}
      >
        <div className="history-head">
          <strong className="history-name">{name}</strong>
          <span className={month.overBudget ? 'over' : 'under'}>
            {month.overBudget
              ? `${formatCents(-month.remainingCents)} over`
              : `${formatCents(month.remainingCents)} left`}
          </span>
        </div>
        <div className="bar" role="presentation">
          <div className={month.overBudget ? 'fill over-fill' : 'fill'} style={{ width: `${pct}%` }} />
        </div>
        <span className="history-foot">
          <span className="meta">
            {formatCents(month.spentCents)} of {formatCents(month.limitCents)} ·{' '}
            {month.transactionCount} {month.transactionCount === 1 ? 'expense' : 'expenses'}
          </span>
          <span className="history-open">
            View in Budget
            <span className="chev" aria-hidden="true">
              ›
            </span>
          </span>
        </span>
      </Link>
    </li>
  );
}
